import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useAppDispatch } from "../../../hooks";
import { updateEmployee } from "../../../slices/employeeSlice";
import type { EmployeeItem } from "../../../types/employee";
import type { Store } from "../../../types/store";

interface Role {
  id: string;
  name: string;
}

interface UpdateRolesAndStoresModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee: EmployeeItem;
  roles: Role[];
  stores: Store[];
}

const UpdateRolesAndStoresModal: React.FC<UpdateRolesAndStoresModalProps> = ({
  isOpen,
  onClose,
  employee,
  roles,
  stores,
}) => {
  const dispatch = useAppDispatch();

  if (!isOpen) return null;

  // ✅ Validation schema
  const validationSchema = Yup.object({
    mainRole: Yup.string().nullable(),
    roleIds: Yup.array().of(Yup.string()),
    storeIds: Yup.array().of(Yup.string()).min(1, "Assign at least one store"),
  });

  // ✅ Initial values
  const initialValues = {
    mainRole: employee.mainRole ?? "",
    roleIds: employee.assignedRoles?.map((r) => r.id),
    storeIds: employee.assignedStores?.map((s) => s.id),
  };

  // ✅ Handle submit
  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const payload = {
        mainRole: values.mainRole || null,
        roleIds: values.roleIds,
        storeIds: values.storeIds,
      };
      await dispatch(updateEmployee({ id: employee.id, data: payload })).unwrap();
      onClose();
    } catch (error) {
      console.error("Failed to update employee roles/stores:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[999]">
      <div className="bg-white w-full max-w-2xl p-6 rounded-2xl shadow-md overflow-y-auto max-h-[90vh]">
        <h3 className="text-lg text-left font-semibold text-gray-800 mb-4">
          Update Roles & Store Assignments
        </h3>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched, isSubmitting }) => (
            <Form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-3">
                {/* --- MAIN ROLE (Single Select) --- */}
                <label className="form-label">
                  Main Role
                  <Field
                    as="select"
                    name="mainRole"
                    value={values.mainRole ?? ""}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const newMain = e.target.value || null;
                      const updatedOtherRoles =
                        values.roleIds?.filter((id) => id !== newMain) || [];
                      setFieldValue("mainRole", newMain);
                      setFieldValue("roleIds", updatedOtherRoles);
                    }}
                    className="form-input mt-1 w-full"
                  >
                    <option value="">Select Main Role</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </Field>
                  {errors.mainRole && touched.mainRole && (
                    <div className="text-xs text-red-500 mt-1">
                      {errors.mainRole as string}
                    </div>
                  )}
                </label>

                {/* --- OTHER ROLES (Multi-select Button Chips) --- */}
                <label className="form-label">
                  Other Roles
                  <div className="flex flex-wrap gap-2 mt-1">
                    {roles.map((r) => {
                      const selected = values.roleIds?.includes(r.id);
                      const isMain = values.mainRole === r.id;

                      return (
                        <button
                          type="button"
                          key={r.id}
                          disabled={isMain}
                          onClick={() => {
                            if (selected) {
                              setFieldValue(
                                "roleIds",
                                values.roleIds?.filter((id) => id !== r.id)
                              );
                            } else if (!isMain) {
                              setFieldValue("roleIds", [
                                ...(values.roleIds ?? []),
                                r.id,
                              ]);
                            }
                          }}
                          className={`px-3 py-1 rounded-full border text-sm transition ${selected
                              ? "bg-primary text-white border-primary"
                              : "bg-gray-100 text-gray-700 border-gray-300"
                            } ${isMain ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          {r.name}
                        </button>
                      );
                    })}
                  </div>
                  {errors.roleIds && touched.roleIds && (
                    <div className="text-xs text-red-500 mt-1">
                      {errors.roleIds as string}
                    </div>
                  )}
                </label>
              </div>

              {/* --- STORES (Multi-select Button Chips) --- */}
              <label className="form-label block">
                Stores
                <div className="flex flex-wrap gap-2 mt-1">
                  {stores.map((s) => {
                    const selected = values.storeIds?.includes(s.id);
                    return (
                      <button
                        type="button"
                        key={s.id}
                        onClick={() => {
                          if (selected) {
                            setFieldValue(
                              "storeIds",
                              values.storeIds?.filter((id) => id !== s.id)
                            );
                          } else {
                            setFieldValue("storeIds", [
                              ...(values.storeIds ?? []),
                              s.id,
                            ]);
                          }
                        }}
                        className={`px-3 py-1 rounded-full border text-sm transition ${selected
                            ? "bg-primary text-white border-primary"
                            : "bg-gray-100 text-gray-700 border-gray-300"
                          } cursor-pointer`}
                      >
                        {s.name} — {s.location}
                      </button>
                    );
                  })}
                </div>

                {errors.storeIds && touched.storeIds && (
                  <div className="text-xs text-red-500 mt-1">
                    {errors.storeIds as string}
                  </div>
                )}
              </label>

              {/* --- BUTTONS --- */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  className="modal-btn-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default UpdateRolesAndStoresModal;
